import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import api from '../../services/api';
import DateTimePicker from '../../components/ui/DateTimePicker';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeft, Save, Globe, Upload, FileText, Loader,
    Lock, AlertTriangle, CheckCircle, Info, File, X, Users, Trash2, Crown
} from 'lucide-react';
import { AnimatePresence, motion as m2 } from 'framer-motion';
import Select from '../../components/ui/Select';

const STATUS_LABELS = {
    draft: 'docs.status_draft',
    published: 'docs.status_published',
    pending_approval: 'docs.status_pending_approval',
    approved: 'docs.status_approved',
    rejected: 'docs.status_rejected',
    locked: 'docs.status_locked',
};

export default function DocEditorPage({ docId = null, embedded = false, onNavigate = null }) {
    const { t } = useTranslation();
    const params = useParams();
    const id = docId || params.id;
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = embedded ? null : (location.pathname.startsWith('/admin') ? '/admin/docs' : '/panel/docs');
    const { user } = useAuth();
    const { currentOrg, currentOrgId } = useOrganization();
    const isNew = id === 'new' || !id;

    // File input ref
    const pdfInputRef = useRef();

    const handleNavigate = (path) => {
        if (embedded && onNavigate) {
            if (path === 'docs_list') onNavigate('docs_list');
            else onNavigate(`doc_${path}`);
        } else if (basePath) {
            if (path === 'docs_list') navigate(basePath);
            else navigate(`${basePath}/${path}`);
        }
    };

    // State
    const [doc, setDoc] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [commentDeadlineAt, setCommentDeadlineAt] = useState('');
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [publishModal, setPublishModal] = useState(false);
    const [changeSummary, setChangeSummary] = useState('');
    const [error, setError] = useState('');
    const [savedAt, setSavedAt] = useState(null);

    // PDF state
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [candidates, setCandidates] = useState([]);
    // Versiyalar
    const [versions, setVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);
    // Permissions modal
    const [assignedUsers, setAssignedUsers] = useState([]); // { userId, name, initials, isApprover }
    const [showPermModal, setShowPermModal] = useState(false);
    const [modalUsers, setModalUsers] = useState([]);
    const [modalOriginal, setModalOriginal] = useState([]);
    const [addUserVal, setAddUserVal] = useState('');
    const [permSaving, setPermSaving] = useState(false);
    const [permError, setPermError] = useState('');

    // Permissions
    const canCreate = user?.is_superadmin || user?.can_create_documents;
    const isOwner = doc && String(doc.created_by_id) === String(user?.id);
    const canEdit = user?.is_superadmin || (user?.can_edit_documents && isOwner);
    const canPublish = canEdit || canCreate;
    const isEditable = isNew ? canCreate : (canEdit && doc && !doc.is_locked);

    useEffect(() => {
        if (!isNew) { fetchDoc(); fetchVersions(); }
        fetchCandidates();
    }, [id]);

    useEffect(() => {
        if (pdfFile) {
            const url = window.URL.createObjectURL(pdfFile);
            setPdfBlobUrl(url);
            return () => window.URL.revokeObjectURL(url);
        } else if (!isNew && id) {
            setPdfLoading(true);
            const endpoint = selectedVersion
                ? `/docs/${id}/pdf?version=${selectedVersion}`
                : `/docs/${id}/pdf`;
            api.get(endpoint, { responseType: 'blob' })
                .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                    setPdfBlobUrl(url);
                })
                .catch(() => {})
                .finally(() => setPdfLoading(false));
        } else {
            setPdfBlobUrl(null);
        }
    }, [id, isNew, pdfFile, doc?.pdf_filename, doc?.current_version, selectedVersion]);

    async function fetchCandidates() {
        try {
            const { data } = await api.get('/docs/approver-candidates');
            setCandidates(data);
        } catch { /* ignore */ }
    }

    async function fetchVersions() {
        try {
            const { data } = await api.get(`/docs/${id}/versions`);
            setVersions(data || []);
        } catch { /* ignore */ }
    }

    async function fetchDoc() {
        setLoading(true);
        try {
            const { data } = await api.get(`/docs/${id}`);
            setDoc(data);
            setTitle(data.title || '');
            setDescription(data.description || '');
            if (data.comment_deadline_at) {
                const dt = new Date(data.comment_deadline_at);
                dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
                setCommentDeadlineAt(dt.toISOString().slice(0, 16));
            } else {
                setCommentDeadlineAt('');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    // ── PDF file select ──
    const handlePdfSelect = (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            setError(t('docs.pdf_only_error', 'Faqat PDF formatdagi fayllar ruxsat etiladi'));
            return;
        }
        setError('');
        setPdfFile(file);
        if (!title.trim()) setTitle(file.name.replace(/\.pdf$/i, ''));
    };

    // ── PDF save ──
    async function handlePdfSave() {
        if (!title.trim()) { setError(t('docs.title_required', 'Sarlavha kiritilmagan')); return; }
        if (isNew && !pdfFile) { setError(t('docs.pdf_required', 'PDF faylini tanlang')); return; }

        setSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('title', title.trim());
            if (description) formData.append('description', description.trim());
            if (commentDeadlineAt) formData.append('comment_deadline_at', commentDeadlineAt);
            if (pdfFile) formData.append('file', pdfFile);

            if (isNew) {
                const viewers = assignedUsers.filter(u => !u.isApprover).map(u => u.userId);
                const approvers = assignedUsers.filter(u => u.isApprover).map(u => u.userId);
                if (viewers.length > 0) formData.append('viewers', JSON.stringify(viewers));
                if (approvers.length > 0) formData.append('approvers', JSON.stringify(approvers));
                if (currentOrgId) formData.append('organization_id', currentOrgId);
                const { data } = await api.post('/docs/upload-pdf', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                handleNavigate(data.id);
            } else {
                const { data } = await api.post(`/docs/${id}/update-pdf`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setDoc(data);
                setPdfFile(null);
                setSelectedVersion(null); // joriy (yangi) versiyaga qaytish
                setSavedAt(new Date());
                fetchVersions(); // versiyalar ro'yxatini yangilash
            }
        } catch (err) {
            setError(err?.response?.data?.detail || err.message || 'Xatolik yuz berdi');
        } finally {
            setSaving(false);
        }
    }

    // ── Permissions modal helpers ──
    function _toInitials(name) {
        return (name || '').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
    }

    async function openPermModal() {
        setPermError('');
        if (!isNew && id) {
            try {
                const [vRes, aRes] = await Promise.all([
                    api.get(`/docs/${id}/viewers`),
                    api.get(`/docs/${id}/approvers`),
                ]);
                const map = {};
                (vRes.data || []).forEach(v => {
                    const cand = candidates.find(c => String(c.id) === String(v.user.id));
                    const name = v.user.full_name ||
                        `${v.user.last_name_ru || ''} ${v.user.first_name_ru || ''}`.trim() ||
                        v.user.login;
                    map[v.user.id] = { userId: v.user.id, name, initials: _toInitials(name), isApprover: false,
                        isHead: v.user.is_head_type || cand?.is_head_type || false,
                        position: cand?.position || '',
                        viewerRecordId: v.id, approverRecordId: null };
                });
                (aRes.data || []).forEach(a => {
                    const cand = candidates.find(c => String(c.id) === String(a.user.id));
                    const name = a.user.full_name ||
                        `${a.user.last_name_ru || ''} ${a.user.first_name_ru || ''}`.trim() ||
                        a.user.login;
                    if (map[a.user.id]) {
                        map[a.user.id].isApprover = true; map[a.user.id].approverRecordId = a.id;
                    } else {
                        map[a.user.id] = { userId: a.user.id, name, initials: _toInitials(name), isApprover: true,
                            isHead: a.user.is_head_type || cand?.is_head_type || false,
                            position: cand?.position || '',
                            viewerRecordId: null, approverRecordId: a.id };
                    }
                });
                const merged = Object.values(map);
                setModalUsers(merged);
                setModalOriginal(JSON.parse(JSON.stringify(merged)));
            } catch { setModalUsers([]); setModalOriginal([]); }
        } else {
            setModalUsers(JSON.parse(JSON.stringify(assignedUsers)));
            setModalOriginal(JSON.parse(JSON.stringify(assignedUsers)));
        }
        setShowPermModal(true);
    }

    function addUserToModal() {
        const user = candidates.find(c => String(c.id) === String(addUserVal));
        if (!user || modalUsers.find(m => String(m.userId) === String(user.id))) return;
        const name = user.full_name || user.login || '';
        setModalUsers(prev => [...prev, { userId: user.id, name, initials: _toInitials(name), isApprover: false, viewerRecordId: null, approverRecordId: null }]);
        setAddUserVal('');
    }

    function removeFromModal(userId) {
        setModalUsers(prev => prev.filter(m => String(m.userId) !== String(userId)));
    }

    function toggleApproverInModal(userId) {
        setModalUsers(prev => prev.map(m => String(m.userId) === String(userId) ? { ...m, isApprover: !m.isApprover } : m));
    }

    async function savePermissions() {
        setPermSaving(true); setPermError('');
        try {
            if (isNew) {
                setAssignedUsers([...modalUsers]);
                setShowPermModal(false);
            } else {
                // diff and apply via API
                const desiredViewerIds = modalUsers.filter(m => !m.isApprover).map(m => String(m.userId));
                const desiredApproverIds = modalUsers.filter(m => m.isApprover).map(m => String(m.userId));
                const origViewers = modalOriginal.filter(m => !m.isApprover);
                const origApprovers = modalOriginal.filter(m => m.isApprover);
                // Add new viewers
                for (const uid of desiredViewerIds) {
                    if (!origViewers.find(m => String(m.userId) === uid)) await api.post(`/docs/${id}/viewers`, { user_id: uid });
                }
                // Add new approvers
                for (const uid of desiredApproverIds) {
                    if (!origApprovers.find(m => String(m.userId) === uid)) await api.post(`/docs/${id}/approvers`, { user_id: uid });
                }
                // Remove viewers no longer needed
                for (const orig of origViewers) {
                    if (!desiredViewerIds.includes(String(orig.userId)) && orig.viewerRecordId)
                        await api.delete(`/docs/${id}/viewers/${orig.viewerRecordId}`);
                }
                // Remove approvers no longer needed
                for (const orig of origApprovers) {
                    if (!desiredApproverIds.includes(String(orig.userId)) && orig.approverRecordId)
                        await api.delete(`/docs/${id}/approvers/${orig.approverRecordId}`);
                }
                setShowPermModal(false);
            }
        } catch (e) {
            setPermError(e?.response?.data?.detail || 'Xatolik yuz berdi');
        } finally {
            setPermSaving(false);
        }
    }

    async function handlePublish() {
        setPublishing(true);
        setError('');
        try {
            await api.post(`/docs/${id}/publish`, { change_summary: changeSummary });
            setPublishModal(false);
            setChangeSummary('');
            await fetchDoc();
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Xatolik yuz berdi');
            setPublishModal(false);
        } finally {
            setPublishing(false);
        }
    }

    // ─────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 text-muted">
                <Loader className="w-6 h-6 animate-spin mr-2" />
                {t('docs.loading', 'Yuklanmoqda...')}
            </div>
        );
    }

    if (!isEditable && !isNew) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted">
                <Lock className="w-12 h-12 opacity-40" />
                <p className="text-sm font-medium">
                    {doc?.is_locked ? t('docs.doc_locked', 'Hujjat qulflangan') : t('docs.no_edit_permission', "Tahrirlash ruxsati yo'q")}
                </p>
                <button onClick={() => handleNavigate(id)} className="text-brand text-sm hover:underline">
                    {t('docs.view_mode', "Ko'rish rejimiga o'tish")}
                </button>
            </div>
        );
    }

    return (
        <>
        <div className={`flex flex-col h-full gap-0 max-w-5xl mx-auto w-full ${embedded ? 'p-6' : ''}`}>

            {/* ── Top bar ── */}
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <button onClick={() => handleNavigate('docs_list')} className="p-2 rounded-xl border border-border hover:bg-surface-2 transition-colors text-muted hover:text-primary">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold">
                            {isNew ? t('docs.new_pdf_title', 'Yangi PDF Hujjat Yuklash') : t('docs.edit_doc', 'Hujjatni tahrirlash')}
                        </h1>
                        <div className="flex items-center gap-2 mt-0.5">
                            {doc && (
                                <span className="text-xs text-muted px-2 py-0.5 rounded-full bg-surface-2 border border-border">
                                    {t(STATUS_LABELS[doc.status])} · v{doc.current_version}
                                </span>
                            )}
                            {savedAt && (
                                <span className="text-xs text-green-500 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    {savedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} {t('docs.saved_at', 'da saqlandi')}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Permissions button */}
                    <button
                        type="button"
                        onClick={openPermModal}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface-2 border border-border hover:bg-surface text-sm font-semibold text-primary transition-colors shadow-sm"
                    >
                        <Users className="w-4 h-4 text-brand" />
                        {t('docs.permissions', 'Ruxsatlar')}
                        {assignedUsers.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-brand/15 text-brand text-xs font-bold">{assignedUsers.length}</span>
                        )}
                    </button>
                    {/* Publish button — faqat mavjud hujjatlar uchun */}
                    {canPublish && !isNew && doc && (
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setPublishModal(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-inverse text-sm font-semibold shadow-brand-sm hover:opacity-90 transition-opacity"
                        >
                            <Globe className="w-4 h-4" />
                            {t('docs.publish', 'Nashr qilish')}
                        </motion.button>
                    )}
                    {/* PDF Save / Upload */}
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={handlePdfSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-surface-2 border border-border text-primary text-sm font-semibold hover:bg-surface transition-colors disabled:opacity-50"
                    >
                        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isNew ? t('docs.upload_and_save', 'PDF Yuklash va Saqlash') : t('docs.save', 'Saqlash')}
                    </motion.button>
                </div>
            </div>

            {/* ── Publish modal ── */}
            {publishModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-md p-6 space-y-4"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
                                <Globe className="w-5 h-5 text-brand" />
                            </div>
                            <div>
                                <h2 className="font-bold text-lg">{t('docs.publish', 'Nashr qilish')}</h2>
                                <p className="text-xs text-muted">{t('docs.publish_desc', 'Yangi versiya yaratiladi va hujjat nashr qilinadi')}</p>
                            </div>
                        </div>
                        <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-xs flex items-start gap-2 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {t('docs.publish_info', 'Nashr qilganda joriy kontent yangi versiya sifatida saqlanadi.')}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wider">
                                {t('docs.change_summary', "O'zgarishlar xulosasi (ixtiyoriy)")}
                            </label>
                            <textarea
                                value={changeSummary}
                                onChange={e => setChangeSummary(e.target.value)}
                                placeholder={t('docs.change_summary_placeholder', "Nima o'zgardi? (masalan: yangi bo'lim qo'shildi)")}
                                rows={3}
                                className="w-full px-3 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setPublishModal(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-surface-2 transition-colors"
                            >
                                {t('docs.cancel', 'Bekor qilish')}
                            </button>
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={handlePublish}
                                disabled={publishing}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand text-inverse text-sm font-semibold shadow-brand-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                                {publishing ? <Loader className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                                {t('docs.publish', 'Nashr qilish')}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ── Error ── */}
            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm mb-3 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* ── Meta fields ── */}
            <div className="flex flex-col gap-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wider">{t('docs.title', 'Sarlavha')}</label>
                        <input
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder={t('docs.title_placeholder', 'Masalan: CAU Ichki Tartib Qoidalari 2026')}
                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 font-semibold"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wider">{t('docs.comment_deadline_title', 'Izoh yozish muddati')}</label>
                        <DateTimePicker
                            value={commentDeadlineAt}
                            onChange={v => setCommentDeadlineAt(v)}
                            placeholder={t('docs.select_datetime', 'Sana va vaqt tanlang...')}
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wider">{t('docs.short_desc', 'Qisqacha tavsif')}</label>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder={t('docs.description_placeholder', 'Hujjat haqida qisqacha ma\'lumot...')}
                        rows={2}
                        className="w-full px-4 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 resize-y"
                    />
                </div>
            </div>

            {/* ── PDF Preview & Replace Zone ── */}
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        <label className="block text-xs font-semibold text-muted uppercase tracking-wider">
                            {t('docs.pdf_file_label', 'PDF Fayl')} <span className="text-error">*</span>
                        </label>
                        {/* Versiya tanlash — faqat mavjud hujjatlarda va yangi fayl tanlanmagan bo'lsa */}
                        {!isNew && !pdfFile && versions.length > 1 && (
                            <div className="w-52">
                                <Select
                                    value={selectedVersion ?? ''}
                                    options={[
                                        { value: '', label: `v${doc?.current_version} — ${t('docs.current_version', 'Joriy versiya')}` },
                                        ...versions.map(v => ({
                                            value: v.version_number,
                                            label: `v${v.version_number} — ${new Date(v.created_at).toLocaleDateString()}`
                                        }))
                                    ]}
                                    onChange={val => setSelectedVersion(val !== '' ? Number(val) : null)}
                                />
                            </div>
                        )}
                    </div>

                    <input
                        ref={pdfInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={e => handlePdfSelect(e.target.files?.[0])}
                        className="hidden"
                    />

                    {/* Zamените button — faqat mavjud hujjatlar uchun (yangi versiya qo'shish) */}
                    {!isNew && (
                        <button
                            type="button"
                            onClick={() => pdfInputRef.current?.click()}
                            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface-2 border border-border hover:bg-surface text-xs font-semibold text-primary transition-colors shadow-sm"
                        >
                            <Upload className="w-4 h-4 text-brand" />
                            {pdfFile ? t('docs.change_selected_pdf', 'Boshqa PDF tanlash') : t('docs.replace_pdf', 'PDF faylni almashtirish')}
                        </button>
                    )}
                </div>

                {/* Selected new PDF notification badge */}
                {pdfFile && (
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-brand/10 border border-brand/20 text-sm">
                        <div className="flex items-center gap-2">
                            <File className="w-5 h-5 text-brand" />
                            <div>
                                <span className="font-semibold text-primary">{pdfFile.name}</span>
                                <span className="text-xs text-muted ml-2">({(pdfFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                <span className="ml-2 text-xs font-bold text-amber-600 dark:text-amber-400">({t('docs.pending_save', 'Saqlash kutilmoqda')})</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setPdfFile(null);
                                if (!isNew && id) {
                                    setPdfLoading(true);
                                    api.get(`/docs/${id}/pdf`, { responseType: 'blob' })
                                        .then(r => {
                                            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                                            setPdfBlobUrl(url);
                                        })
                                        .catch(() => {})
                                        .finally(() => setPdfLoading(false));
                                }
                            }}
                            className="p-1 rounded-lg hover:bg-surface-2 text-muted hover:text-error transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* PDF Viewer container */}
                <div className="bg-surface border border-border rounded-2xl overflow-hidden min-h-[600px] flex flex-col" style={{ height: 'calc(100vh - 360px)' }}>
                    {pdfLoading ? (
                        <div className="w-full h-full flex items-center justify-center text-muted">
                            <Loader className="w-6 h-6 animate-spin mr-2" />
                            {t('docs.pdf_loading', 'PDF yuklanmoqda...')}
                        </div>
                    ) : pdfBlobUrl ? (
                        <iframe
                            src={pdfBlobUrl}
                            className="w-full h-full"
                            title={title || doc?.title}
                            style={{ border: 'none' }}
                        />
                    ) : isNew ? (
                        <div
                            onClick={() => pdfInputRef.current?.click()}
                            className="cursor-pointer w-full h-full flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-border rounded-xl hover:border-brand/50 hover:bg-brand/5 transition-all"
                        >
                            <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                                <Upload className="w-6 h-6 text-brand" />
                            </div>
                            <div className="text-center">
                                <p className="font-medium text-primary text-sm">
                                    {t('docs.drag_pdf_here', 'PDF faylni bu yerga tashlang yoki tanlash uchun bosing')}
                                </p>
                                <p className="text-xs text-muted mt-1">{t('docs.pdf_only_hint', 'Faqat .pdf fayllar qabul qilinadi')}</p>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>

        {/* ── Permissions Modal ── */}
        <AnimatePresence>
            {showPermModal && (
                <m2.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowPermModal(false); }}
                >
                    <m2.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal header */}
                        <div className="flex items-start justify-between p-5 border-b border-border rounded-t-2xl bg-surface">
                            <div>
                                <h2 className="text-base font-bold text-primary flex items-center gap-2">
                                    <Users className="w-5 h-5 text-brand" />
                                    {t('docs.access_rights', 'Huquqlarni boshqarish')}
                                </h2>
                                <p className="text-xs text-muted mt-0.5 uppercase tracking-wider">
                                    {doc?.title || 'Governance'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowPermModal(false)}
                                className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-primary transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="p-5 space-y-4">
                            {/* Add user */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-muted uppercase tracking-wider">
                                    {t('docs.add_user', 'Foydalanuvchi qo\'shish')}
                                </label>
                                <Select
                                    searchable
                                    placeholder={t('docs.select_user_placeholder', 'Foydalanuvchini tanlang...')}
                                    value={addUserVal}
                                    options={[
                                        { value: '', label: t('docs.select_user_placeholder', 'Foydalanuvchini tanlang...') },
                                        ...candidates
                                            .filter(c => !modalUsers.find(m => String(m.userId) === String(c.id)))
                                            .map(c => ({ value: String(c.id), label: c.full_name || c.login }))
                                    ]}
                                    onChange={val => {
                                        if (!val) return;
                                        const user = candidates.find(c => String(c.id) === String(val));
                                        if (!user || modalUsers.find(m => String(m.userId) === String(user.id))) return;
                                        const name = user.full_name || user.login || '';
                                        setModalUsers(prev => [...prev, {
                                            userId: user.id,
                                            name,
                                            initials: _toInitials(name),
                                            isApprover: false,
                                            isHead: user.is_head_type || false,
                                            position: user.position || '',
                                            viewerRecordId: null,
                                            approverRecordId: null,
                                        }]);
                                        setAddUserVal('');
                                    }}
                                />
                            </div>

                            {/* User list */}
                            {modalUsers.length > 0 && (
                                <div className="space-y-1">
                                    {/* Column headers */}
                                    <div className="flex items-center px-3 pb-1">
                                        <span className="flex-1 text-xs font-bold text-muted uppercase tracking-wider">
                                            {t('docs.assigned_users', 'Tayinlangan foydalanuvchilar')}
                                        </span>
                                        <span className="text-xs font-bold text-muted uppercase tracking-wider w-24 text-center">
                                            {t('docs.approves', 'Tasdiqlaydi')}
                                        </span>
                                        <span className="w-8" />
                                    </div>
                                    <div className="space-y-1 max-h-56 overflow-y-auto">
                                        {modalUsers.map(u => (
                                            <div
                                                key={u.userId}
                                                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface-2 border border-border"
                                            >
                                                {/* Avatar */}
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 bg-brand/20 text-brand`}>
                                                    {u.initials || '?'}
                                                </div>
                                                {/* Name + badges + position */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <p className="text-sm font-semibold text-primary truncate">{u.name}</p>
                                                        {doc?.created_by_id && String(u.userId) === String(doc.created_by_id) && (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-brand/15 text-brand border border-brand/20 flex-shrink-0">АВТОР</span>
                                                        )}
                                                         {u.isHead && (
                                                             <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0" title="Head — tasdiqlash statusi">
                                                                 <Crown className="w-3 h-3" />
                                                             </span>
                                                         )}
                                                    </div>
                                                    <p className="text-xs text-muted">{u.position || (u.isApprover ? t('docs.approver_role', 'Tasdiqlovchi') : t('docs.viewer_role', "Ko'ruvchi"))}</p>
                                                </div>
                                                {/* Toggle */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleApproverInModal(u.userId)}
                                                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${u.isApprover ? 'bg-brand' : 'bg-border'}`}
                                                >
                                                    <span className={`absolute top-0.5 left-0 w-5 h-5 rounded-full bg-white shadow transition-transform ${u.isApprover ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                                </button>
                                                {/* Delete */}
                                                <button
                                                    type="button"
                                                    onClick={() => removeFromModal(u.userId)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-border text-muted hover:text-red-500 hover:border-red-400/40 hover:bg-red-500/5 transition-colors flex-shrink-0"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Error */}
                            {permError && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs border border-red-200 dark:border-red-800">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                    {permError}
                                </div>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div className="flex items-center justify-between px-5 py-4 border-t border-border bg-surface-2 rounded-b-2xl">
                            <span className="text-xs text-muted">
                                {modalUsers.length} {t('docs.viewers_count', "ko'radi")} · {modalUsers.filter(m => m.isApprover).length} {t('docs.approvers_count', 'tasdiqlaydi')}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowPermModal(false)}
                                    className="px-4 py-2 rounded-xl border border-border text-sm font-semibold text-primary hover:bg-surface transition-colors"
                                >
                                    {t('docs.cancel', 'Bekor qilish')}
                                </button>
                                <button
                                    type="button"
                                    onClick={savePermissions}
                                    disabled={permSaving}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    {permSaving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                                    {t('docs.save', 'Saqlash')}
                                </button>
                            </div>
                        </div>
                    </m2.div>
                </m2.div>
            )}
        </AnimatePresence>
        </>
    );
}